import PropTypes from 'prop-types';

import {
  Box, Heading, SimpleGrid, Skeleton,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component} from '@amblique/king-living/app/components/contentful/component';
import {BlogSummary} from '@amblique/king-living/app/components/contentful/blog-summary';
import {Link as ContentfulLink} from '@amblique/king-living/app/contentful/models/link';

/**
 * BlogGrid Style 1
 * Featured layout with title, hero blog summary, grid of 3 blog summaries, and CTA
 *
 * @param {Object} props - Component props
 * @param {Object} props.component - BlogGrid component data
 * @param {string} props.component.title - Section title
 * @param {Object[]} props.component.blogSummariesCollection - Collection of blog summaries
 * @param {Object} props.component.cta - CTA link component
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @param {Object} props.styles - Component styles
 * @returns {React.ReactElement}
 */
export const BlogGridStyle1 = ({component, isComplete = true, isFetching = false, styles, ...props}) => {
  const {title, blogSummaryCollection, cta} = component;
  const blogSummaries = blogSummaryCollection?.items || [];

  // First item is the featured/hero blog summary
  const featuredBlog = blogSummaries[0];
  // Remaining items are displayed in a 3-column grid
  const gridBlogs = blogSummaries.slice(1);

  return (
    <Box {...styles.container}>
      {/* Section Title */}
      {title && (
        <Heading {...styles.title}>{title}</Heading>
      )}

      {/* Featured Blog Summary - First Row */}
      {!isComplete && isFetching ? (
        <Skeleton height="400px" width="100%" />
      ) : featuredBlog && (
        <Box {...styles.featuredWrapper}>
          <Component
            as={BlogSummary}
            component={featuredBlog}
            isComplete={isComplete}
            isFetching={isFetching}
            variant="featured"
          />
        </Box>
      )}

      {/* Blog Grid - 3 Columns */}
      {gridBlogs.length > 0 && (
        <SimpleGrid {...styles.blogGrid}>
          {gridBlogs.map((blog, index) => (
            <Component
              key={blog.sys?.id || index}
              as={BlogSummary}
              component={blog}
              isComplete={isComplete}
              isFetching={isFetching}
            />
          ))}
        </SimpleGrid>
      )}

      {/* CTA Link */}
      {cta && (
        <Box {...styles.ctaContainer}>
          <ContentfulLink
            component={cta}
          />
        </Box>
      )}
    </Box>
  );
};

BlogGridStyle1.displayName = 'BlogGridStyle1';
BlogGridStyle1.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  styles: PropTypes.object.isRequired,
};
